import { useAppStore } from '../context/AppContext';
import useMissingTasksApi from '../api/useMissingTasksApi';
import { formatDateTime } from 'odfjell-date-time-utils/dateTimeFormatter';
import { isDateAfter, isDateSame, isDateSameOrAfter, subtractDateDays } from 'odfjell-date-time-utils/dateTimeUtils';
import useGetBunkeringData from 'src/api/bunkering/useGetBunkeringData';
import { Bunkering } from 'src/types/ViewModel/Bunkering/Bunkering';
import { BunkeringEvent } from 'src/types/ViewModel/Bunkering/BunkeringEvent';
import { FuelROB } from 'src/types/ViewModel/Bunkering/FuelROB';
import { UUID } from 'crypto';
import { NoonReportStatus } from 'src/types/ViewModel/Aol/NoonReportStatus';
import { AppFeatureEnum } from 'src/enums/appFeatureEnum';
import useAppFeature from './useAppFeature';
import useCheckStowageIntegration from './useCheckStowageIntegration';
import { useStowagePlanStore } from 'src/context/StowagePlanContext';
import { useEventStore } from 'src/context/EventContext';
import { populateCargoOnBoardFromStowage } from 'src/utils/cargo-temp-log/cargoTempLogUtils';
import { useVesselStore } from 'src/context/VesselContext';

export default () => {
    const { getLatestCargoTempLogDates, getLatestNoonReportStatuses, getLatestRobStatuses } = useMissingTasksApi();
    const { getBunkeringEvents, getBunkerings, getFuelRobData } = useGetBunkeringData();
    const {
        setMissingCargoTempLogStatuses,
        setMissingRobStatuses,
        setMissingNoonReportStatuses,
        setNumMissingBunkerRegistrations,
        setNumMissingBunkerRob,
    } = useAppStore();
    const { appFeatureSettings } = useAppStore();
    const { loadedMasterStowagePlan } = useStowagePlanStore();
    const { cargoOperationsEvents } = useEventStore();
    const { selectedVessel } = useVesselStore();
    const { getAppFeatureFromDateForVessel } = useAppFeature();
    const { checkLoadedStowageIntegration } = useCheckStowageIntegration();

    const FROM_DATE = subtractDateDays(new Date(), 13);
    const TO_DATE = new Date();
    const fromDateStr = formatDateTime(FROM_DATE, 'yyyyMMdd');
    const toDateStr = formatDateTime(TO_DATE, 'yyyyMMdd');

    const FROM_DATE_BUNKERING = new Date();
    FROM_DATE_BUNKERING.setFullYear(FROM_DATE_BUNKERING.getFullYear() - 1);
    const fromDateStrBunkering = formatDateTime(FROM_DATE_BUNKERING, 'yyyyMMdd');

    const updateMissingNoonReportStatuses = async (imoNumber: number): Promise<NoonReportStatus[] | undefined> => {
        const noonReportStatusList = await getLatestNoonReportStatuses(imoNumber, fromDateStr, toDateStr);
        if (noonReportStatusList && noonReportStatusList.length > 0) {
            setMissingNoonReportStatuses([...noonReportStatusList]);
        } else if (noonReportStatusList && noonReportStatusList.length === 0) {
            setMissingNoonReportStatuses([]);
        } else {
            setMissingNoonReportStatuses(null);
        }

        return noonReportStatusList;
    };

    const updateMissingRobStatuses = async (imoNumber: number) => {
        const missingRobStatuses = await getLatestRobStatuses(imoNumber, fromDateStr, toDateStr);
        if (missingRobStatuses && missingRobStatuses.length > 0) {
            setMissingRobStatuses([...missingRobStatuses]);
        } else if (missingRobStatuses && missingRobStatuses.length === 0) {
            setMissingRobStatuses([]);
        } else {
            setMissingRobStatuses(null);
        }
    };

    const updateMissingBunkerRegistrations = async (imoNumber: number): Promise<Bunkering[] | undefined> => {
        const bunkeringEvents = (await getBunkeringEvents(imoNumber, fromDateStrBunkering, toDateStr, true, true)) as BunkeringEvent[] | undefined;
        const bunkerings = (await getBunkerings(imoNumber, fromDateStrBunkering, toDateStr, true)) as Bunkering[] | undefined;
        if (!Array.isArray(bunkeringEvents) || !Array.isArray(bunkerings)) {
            setNumMissingBunkerRegistrations(null);
            return undefined;
        }

        const missingBunkeringRegistrations = bunkeringEvents.filter(
            (event) =>
                !event.debunkering &&
                isDateSameOrAfter(event.bunkeringDateTimeLocal, FROM_DATE_BUNKERING) &&
                !bunkerings.some((bunkering) => bunkering.eventId === event.eventID)
        );
        const missingDebunkeringRegistrations = bunkeringEvents.filter(
            (event) =>
                event.debunkering &&
                isDateSameOrAfter(event.bunkeringDateTimeLocal, FROM_DATE_BUNKERING) &&
                !bunkerings.some((bunkering) => bunkering.debunkeringEventIds.includes(event.eventID as UUID))
        );
        setNumMissingBunkerRegistrations(missingBunkeringRegistrations.length + missingDebunkeringRegistrations.length);

        return bunkerings;
    };

    const updateMissingBunkerRob = async (
        imoNumber: number,
        bunkerings: Bunkering[] | undefined,
        noonReportStatusList: NoonReportStatus[] | undefined
    ): Promise<void> => {
        if (!Array.isArray(bunkerings) || !Array.isArray(noonReportStatusList)) {
            setNumMissingBunkerRob(null);
            return;
        }

        const bunkeringsInPeriod = bunkerings.filter((b) => isDateSameOrAfter(b.bunkeringDateTime, FROM_DATE)).map((b) => b.bunkeringGuid);
        const robs = (await getFuelRobData(
            imoNumber,
            noonReportStatusList.map((status) => status.noonReportId),
            bunkeringsInPeriod,
            true,
            true
        )) as FuelROB[] | undefined;
        if (!Array.isArray(robs)) {
            setNumMissingBunkerRob(null);
            return;
        }

        let missingRobs = 0;
        bunkeringsInPeriod.forEach((bunkeringGuid) => {
            if (!robs.some((rob) => !rob.noonReportId && rob.bunkeringGuid === bunkeringGuid)) {
                missingRobs++;
            }
        });
        const appfeature = appFeatureSettings.find((feature) => feature.appFeatureId === AppFeatureEnum.Bunkering);
        const appFeatureVessel = appfeature?.vessels?.find((vessel) => vessel.imoNumber === imoNumber.toString());
        const activationDate = appFeatureVessel?.fromDate ?? appfeature?.fromDate;
        if (!activationDate) {
            setNumMissingBunkerRob(null);
            return;
        }

        // create an array of dates from activationDate to today or from 13 days ago if activation date is more than 13 days ago
        const dates = [];
        const today = new Date();
        const todayMinus13Days = subtractDateDays(today, 13);
        const startDate = isDateAfter(activationDate, todayMinus13Days) ? activationDate : todayMinus13Days;
        for (let d = new Date(startDate); d <= today; d.setDate(d.getDate() + 1)) {
            dates.push(new Date(d));
        }

        dates.forEach((date) => {
            const noonReportStatuses = noonReportStatusList.filter((status) => isDateSame(status.noonReportDate, date, 'day'));
            if (noonReportStatuses.length === 0) {
                missingRobs++;
            } else {
                noonReportStatuses.forEach((noonReportStatus) => {
                    if (!robs.some((rob) => rob.noonReportId === noonReportStatus.noonReportId)) {
                        missingRobs++;
                        return;
                    }
                });
            }
        });
        setNumMissingBunkerRob(missingRobs);
    };

    const updateAllMissingTasks = async (imoNumber: number) => {
        const [noonReportStatusList, bunkerings] = await Promise.all([
            updateMissingNoonReportStatuses(imoNumber),
            updateMissingBunkerRegistrations(imoNumber),
            updateMissingRobStatuses(imoNumber),
            updateCargoTempLogMissingTasks(imoNumber),
        ]);

        await updateMissingBunkerRob(imoNumber, bunkerings, noonReportStatusList);
    };

    const updateAolMissingTasks = async (imoNumber: number) => {
        const noonReportStatusList = await getLatestNoonReportStatuses(imoNumber, fromDateStr, toDateStr);
        if (noonReportStatusList && noonReportStatusList.length > 0) {
            setMissingNoonReportStatuses([...noonReportStatusList]);
        } else if (noonReportStatusList && noonReportStatusList.length === 0) {
            setMissingNoonReportStatuses([]);
        } else {
            setMissingNoonReportStatuses(null);
        }
    };

    const updateCargoTempLogMissingTasks = async (imoNumber: number) => {
        const stowageIntegrationFromDate = getAppFeatureFromDateForVessel({
            featureEnum: AppFeatureEnum.StowageIntegration,
            imoNumber: imoNumber,
        });
        const logDates = await getLatestCargoTempLogDates(imoNumber, fromDateStr, toDateStr);
        const isStowageIntegrationEnabled = !checkLoadedStowageIntegration(imoNumber);

        if (isStowageIntegrationEnabled && cargoOperationsEvents && cargoOperationsEvents.length > 0 && loadedMasterStowagePlan) {
            populateCargoOnBoardFromStowage(logDates, loadedMasterStowagePlan, stowageIntegrationFromDate, cargoOperationsEvents, selectedVessel);
        }
        if (logDates && logDates.length > 0) {
            setMissingCargoTempLogStatuses([...logDates]);
        } else if (logDates && logDates.length === 0) {
            setMissingCargoTempLogStatuses([]);
        } else {
            setMissingCargoTempLogStatuses(null);
        }
    };

    const updateCargoConsumablesMissingTasks = async (imoNumber: number) => {
        const missingRobStatuses = await getLatestRobStatuses(imoNumber, fromDateStr, toDateStr);
        if (missingRobStatuses && missingRobStatuses.length > 0) {
            setMissingRobStatuses([...missingRobStatuses]);
        } else if (missingRobStatuses && missingRobStatuses.length === 0) {
            setMissingRobStatuses([]);
        } else {
            setMissingRobStatuses(null);
        }
    };

    return {
        updateAllMissingTasks,
        updateCargoTempLogMissingTasks,
        updateAolMissingTasks,
        updateCargoConsumablesMissingTasks,
    };
};
